import {
  Anchor,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Grid,
  Group,
  Input,
  Paper,
  Radio,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconMailCheck, IconSearch } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { api, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { IME_APLIKACIJE } from '../ime';
import { denar } from '../oblika/oblika';
import { NAZIVI_PAKETOV, OPISI_PAKETOV, paketIzParametra, type Paket } from '../seja/paket';
import { TemnoOzadje } from '../postavitev/TemnoOzadje';
import { NapakaVstopa, NazajNaPrijavo } from './vstop';

type Podjetje = Shema['PodjetjeIzRegistra'];

const VIRI = [
  { value: 'GOOGLE', label: 'Iskanje na spletu (Google …)' },
  { value: 'PRIPOROCILO', label: 'Priporočilo' },
  { value: 'DRUZBENA_OMREZJA', label: 'Facebook ali Instagram' },
  { value: 'SEJEM', label: 'Sejem ali dogodek' },
  { value: 'DRUGO', label: 'Drugo' },
];

const prazno = {
  naziv: '',
  nazivKratek: '',
  ulica: '',
  hisnaStevilka: '',
  postnaStevilka: '',
  kraj: '',
  ime: '',
  priimek: '',
  email: '',
  telefon: '',
  virOpis: '',
};

/**
 * Registracija nove stranke (samo podjetja): davčna številka → podatki iz registra FURS (popravljivi), uporabnik
 * (e-pošta je uporabniško ime in kontakt podjetja), paket (samo, če strežnik ponudi več kot enega; privzeto iz
 * {@code ?paket=} – npr. {@code /registracija?paket=DELAVNICA} s strani za delavnice –, sicer Prodaja), vir in soglasje.
 * Strežnik vse preveri in pošlje povezavo za geslo;
 * instanca s preizkusom nastane ob potrditvi ({@link PotrditevRegistracije}).
 */
export function Registracija() {
  const [davcna, setDavcna] = useState('');
  const [podjetje, setPodjetje] = useState<Podjetje | null>(null);
  const [iscem, setIscem] = useState(false);
  const [p, setP] = useState(prazno);
  const [vir, setVir] = useState<string | null>(null);
  const [parametri] = useSearchParams();
  const [paket, setPaket] = useState<Paket>(paketIzParametra(parametri.get('paket')) ?? 'PRODAJA');
  const paketi = useQuery({
    queryKey: ['registracija', 'paketi'],
    queryFn: ({ signal }) => podatki(api.GET('/api/registracija/paketi', { signal })),
    staleTime: Infinity,
    retry: false,
  });
  // brez izbire (samo en paket ali napaka) strežnik vzame Prodajo
  const izbira = (paketi.data ?? []).length > 1 ? (paketi.data ?? []) : [];
  // paket, ki ga strežnik ne ponuja (npr. iz povezave), nadomesti prvi ponujeni
  const izbran = izbira.some((c) => c.paket === paket) ? paket : (izbira[0]?.paket ?? paket);
  const [soglasje, setSoglasje] = useState(false);
  const [past, setPast] = useState('');
  const [napake, setNapake] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [posiljam, setPosiljam] = useState(false);
  const [poslano, setPoslano] = useState<Shema['RegistracijaOdgovor'] | null>(null);

  const polje = (k: keyof typeof prazno) => ({
    value: p[k],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setP({ ...p, [k]: e.currentTarget.value }),
    error: napake[k],
  });

  const isci = async () => {
    setIscem(true);
    setNapake({});
    setNapaka(null);
    try {
      const n = await podatki(api.GET('/api/registracija/podjetje', { params: { query: { davcna } } }));
      setPodjetje(n);
      setP({
        ...p,
        naziv: n.naziv ?? '',
        nazivKratek: n.nazivKratek ?? '',
        ulica: n.ulica ?? '',
        hisnaStevilka: n.hisnaStevilka ?? '',
        postnaStevilka: n.postnaStevilka ?? '',
        kraj: n.kraj ?? '',
      });
    } catch (e) {
      setPodjetje(null);
      if (e instanceof NapakaApi && e.polja.davcna) {
        setNapake({ davcna: e.polja.davcna });
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setIscem(false);
    }
  };

  const oddaj = async (e: FormEvent) => {
    e.preventDefault();
    if (!podjetje) {
      void isci();
      return;
    }
    setPosiljam(true);
    setNapake({});
    setNapaka(null);
    try {
      setPoslano(
        await podatki(
          api.POST('/api/registracija', {
            body: {
              ...p,
              davcna,
              vir: vir ?? undefined,
              soglasje,
              spletnaStran: past || undefined,
              paket: izbira.length > 0 ? izbran : undefined,
            },
          }),
        ),
      );
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setNapake(e.polja);
      } else {
        setNapaka(sporocilo(e));
      }
    } finally {
      setPosiljam(false);
    }
  };

  if (poslano) {
    return (
      <TemnoOzadje>
        <Paper radius="lg" p={36} shadow="xl">
          <Stack gap="md">
            <IconMailCheck size={40} stroke={1.5} />
            <Title order={2} size="h3">
              Preverite e-pošto
            </Title>
            <Text size="sm">
              Na <b>{poslano.email}</b> smo poslali povezavo za nastavitev gesla. Ko geslo nastavite, je vaš račun
              pripravljen in preizkus se začne.
            </Text>
            <Text size="sm" c="dimmed">
              Povezava velja 24 ur. Sporočila ni? Preverite mapo z neželeno pošto ali se registrirajte znova.
            </Text>
            <NazajNaPrijavo />
          </Stack>
        </Paper>
      </TemnoOzadje>
    );
  }

  return (
    <TemnoOzadje sirina={560}>
      <Paper radius="lg" p={{ base: 20, sm: 36 }} shadow="xl">
        <form onSubmit={(e) => void oddaj(e)} noValidate>
          <Stack gap="md">
            <div>
              <Title order={2} size="h3">
                Brezplačni preizkus
              </Title>
              <Text size="sm" c="dimmed">
                Registrirajte podjetje in {IME_APLIKACIJE} 3 dni preizkusite brez obveznosti.
              </Text>
            </div>

            <Input.Wrapper
              id="davcna"
              label="Davčna številka podjetja"
              description="Podatke podjetja izpolnimo iz javnega poslovnega registra."
              error={napake.davcna}
              inputWrapperOrder={['label', 'input', 'description', 'error']}
              size="md"
            >
              <Group gap="xs" wrap="nowrap" align="flex-start">
                <Input
                  id="davcna"
                  size="md"
                  placeholder="12345678"
                  value={davcna}
                  onChange={(e) => {
                    setDavcna(e.currentTarget.value);
                    setPodjetje(null);
                  }}
                  error={!!napake.davcna}
                  inputMode="numeric"
                  autoFocus
                  style={{ flex: 1 }}
                />
                <Button
                  variant={podjetje ? 'default' : 'filled'}
                  leftSection={<IconSearch size={18} />}
                  onClick={() => void isci()}
                  loading={iscem}
                >
                  Poišči
                </Button>
              </Group>
            </Input.Wrapper>

            {podjetje && (
              <>
                <Group gap={6}>
                  <Badge variant="light">{podjetje.vrstaSubjekta === 'F' ? 's.p.' : 'pravna oseba'}</Badge>
                  <Badge variant="light" color={podjetje.zavezanecZaDdv ? 'green' : 'gray'}>
                    {podjetje.zavezanecZaDdv ? `zavezanec za DDV (${podjetje.idZaDdv})` : 'ni zavezanec za DDV'}
                  </Badge>
                </Group>
                <TextInput label="Naziv podjetja" {...polje('naziv')} />
                <TextInput
                  label="Kratek naziv"
                  description="Neobvezno, za glavo dokumentov"
                  {...polje('nazivKratek')}
                />
                <Grid gap="sm">
                  <Grid.Col span={{ base: 12, xs: 8 }}>
                    <TextInput label="Ulica ali naselje" {...polje('ulica')} />
                  </Grid.Col>
                  <Grid.Col span={{ base: 6, xs: 4 }}>
                    <TextInput label="Hišna številka" {...polje('hisnaStevilka')} />
                  </Grid.Col>
                  <Grid.Col span={{ base: 6, xs: 4 }}>
                    <TextInput label="Pošta" inputMode="numeric" {...polje('postnaStevilka')} />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, xs: 8 }}>
                    <TextInput label="Kraj" {...polje('kraj')} />
                  </Grid.Col>
                </Grid>
                <Text size="xs" c="dimmed">
                  Podatke lahko popravite tudi kasneje v nastavitvah.
                </Text>

                {izbira.length > 0 && (
                  <>
                    <Divider label="Paket" labelPosition="left" mt="xs" />
                    <Radio.Group value={izbran} onChange={(v) => setPaket(v as Paket)} error={napake.paket}>
                      <Stack gap="xs">
                        {izbira.map((c) =>
                          c.paket ? (
                            <Radio.Card key={c.paket} value={c.paket} radius="md" p="sm">
                              <Group wrap="nowrap" align="flex-start" gap="sm">
                                <Radio.Indicator mt={2} />
                                <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                                  <Group justify="space-between" gap="xs">
                                    <Text fw={700}>{NAZIVI_PAKETOV[c.paket]}</Text>
                                    <Text size="sm" fw={600}>
                                      {denar(c.cena)} € / mesec
                                    </Text>
                                  </Group>
                                  <Text size="sm" c="dimmed">
                                    {OPISI_PAKETOV[c.paket]}
                                  </Text>
                                </Stack>
                              </Group>
                            </Radio.Card>
                          ) : null,
                        )}
                      </Stack>
                    </Radio.Group>
                    <Text size="xs" c="dimmed">
                      Cene so z DDV. Paket lahko kasneje razširite na zaslonu Naročnina.
                    </Text>
                  </>
                )}

                <Divider label="Vaši podatki" labelPosition="left" mt="xs" />
                <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="sm">
                  <TextInput label="Ime" autoComplete="given-name" {...polje('ime')} />
                  <TextInput label="Priimek" autoComplete="family-name" {...polje('priimek')} />
                </SimpleGrid>
                <TextInput
                  label="E-pošta"
                  description="Z njo se boste prijavljali; je tudi kontakt podjetja."
                  type="email"
                  autoComplete="email"
                  {...polje('email')}
                />
                <TextInput
                  label="Telefon"
                  description="Neobvezno"
                  type="tel"
                  autoComplete="tel"
                  {...polje('telefon')}
                />
                <Select
                  label="Kje ste izvedeli za nas?"
                  description="Neobvezno"
                  placeholder="Izberite"
                  data={VIRI}
                  value={vir}
                  onChange={setVir}
                  clearable
                  error={napake.vir}
                  comboboxProps={{ withinPortal: true }}
                />
                {vir === 'DRUGO' && <TextInput label="Kje?" {...polje('virOpis')} />}
                {/* skrito polje proti robotom: ljudje ga ne vidijo, roboti ga izpolnijo */}
                <Box
                  aria-hidden
                  style={{ position: 'absolute', left: -10000, width: 1, height: 1, overflow: 'hidden' }}
                >
                  <label>
                    Spletna stran
                    <input
                      name="spletnaStran"
                      tabIndex={-1}
                      autoComplete="off"
                      value={past}
                      onChange={(e) => setPast(e.currentTarget.value)}
                    />
                  </label>
                </Box>
                <Checkbox
                  label={
                    <>
                      Strinjam se s{' '}
                      <Anchor href="/splosni-pogoji" target="_blank" rel="noreferrer" inherit>
                        splošnimi pogoji
                      </Anchor>{' '}
                      in pogodbo o obdelavi osebnih podatkov.
                    </>
                  }
                  checked={soglasje}
                  onChange={(e) => setSoglasje(e.currentTarget.checked)}
                  error={napake.soglasje}
                />
              </>
            )}
            {napaka && <NapakaVstopa>{napaka}</NapakaVstopa>}
            {podjetje && (
              <Button type="submit" fullWidth loading={posiljam}>
                Začni brezplačni preizkus
              </Button>
            )}
            <NazajNaPrijavo besedilo="Že imate račun? Prijavite se" />
          </Stack>
        </form>
      </Paper>
    </TemnoOzadje>
  );
}
